import {
  AgendaAppointment,
  AgendaAppointmentPayload,
  AgendaAppointmentStatus,
  AgendaAvailabilityPeriod,
  AgendaAvailabilityResponse,
  AgendaConfig,
  AgendaException,
  AgendaProfessional,
  AgendaService,
  BotStatus,
  Conversation,
  MenuItem,
  Message,
  Promotion,
  RestaurantSettings,
} from '../types';
import api from '../../../../services/api';

const mockPromotions: Record<string, Promotion> = {
  monday: {
    attractions: 'MPB ao vivo com João e Maria',
    promotions: '2x1 em drinks selecionados das 18h às 20h',
  },
  tuesday: {
    attractions: 'Noite de Jazz com Trio Melody',
    promotions: '30% de desconto em vinhos selecionados',
  },
  wednesday: {
    attractions: 'Encontro de Negócios',
    promotions: 'Almoço executivo com sobremesa grátis',
  },
  thursday: {
    attractions: 'Música ambiente selecionada',
    promotions: 'Happy Hour estendido até 21h',
  },
  friday: {
    attractions: 'DJ convidado a partir das 22h',
    promotions: 'Chopp em dobro até 20h',
  },
  saturday: {
    attractions: 'Banda ao vivo a partir das 20h',
    promotions: 'Rodízio de pizzas com 10% de desconto',
  },
  sunday: {
    attractions: 'Música ambiente para almoço em família',
    promotions: 'Crianças até 10 anos não pagam',
  },
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getData = async <T = any>(url: string, config?: Record<string, any>) => {
  const response = await api.get(url, config);
  return response.data as T;
};

const postData = async <T = any>(url: string, payload?: any, config?: Record<string, any>) => {
  const response = await api.post(url, payload, config);
  return response.data as T;
};

const putData = async <T = any>(url: string, payload?: any, config?: Record<string, any>) => {
  const response = await api.put(url, payload, config);
  return response.data as T;
};

const patchData = async <T = any>(url: string, payload?: any, config?: Record<string, any>) => {
  const response = await api.patch(url, payload, config);
  return response.data as T;
};

const deleteData = async <T = any>(url: string, config?: Record<string, any>) => {
  const response = await api.delete(url, config);
  return response.data as T;
};

const mapConversation = (item: any): Conversation => ({
  id: item.cliente_id,
  name: item.nome,
  phoneNumber: item.telefone,
  chat_name: item.chat_name,
  apelido: item.apelido,
  photo: item.photo,
  lastMessage: item.mensagem,
  lastMessageTime: new Date(item.data).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  lastMessageDate: new Date(item.data).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }),
  botActive: item.ativo ?? true,
  transbordo: item.transbordo ?? false,
  unseen: item.unseen ?? false,
  unreadCount: Number(item.unreadCount ?? (item.unseen ? 1 : 0)),
});

const normalizePhoneDigits = (phone: string): string => String(phone || '').replace(/\D/g, '');

const toDddLast8Key = (phone: string): string => {
  const digits = normalizePhoneDigits(phone);
  const normalized = digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;
  const ddd = normalized.slice(0, 2);
  const last8 = normalized.slice(-8);
  return `${ddd}${last8}`;
};

export const fetchBotStatus = async (): Promise<BotStatus> => {
  const data = await getData<any>('/atendabot/config');

  return {
    atendabot_active: data.atendabot_active,
    isActive: data.atendabot_active,
    integration_sischef_active: data.integration_sischef_active,
    integration_sischef_token: data.integration_sischef_token,
  };
};

export const fetchRestaurant = async (restauranteId?: number | string): Promise<BotStatus> => {
  return getData('/atendabot/config', {
    params: restauranteId ? { restaurante_id: restauranteId } : undefined,
  });
};

export const fetchZapiStatus = async (restauranteId?: number | string) => {
  return getData<{
    configured?: boolean;
    connected?: boolean;
    smartphoneConnected?: boolean;
    status?: string;
    provider_status?: string | null;
    phone_number?: string | null;
    connected_phone?: string | null;
    phone_mismatch?: boolean;
    trial_expires_at?: string | null;
    is_subscribed?: boolean;
    phone_change_allowed?: boolean;
    instance_unavailable?: boolean;
    error?: string | null;
  }>('/atendabot/status', {
    params: restauranteId ? { restaurante_id: restauranteId } : undefined,
  });
};

export const connectWhatsApp = async (phoneNumber: string) => {
  return postData<{
    configured: boolean;
    status: string;
    phone_number: string;
    trial_expires_at?: string | null;
    is_subscribed: boolean;
  }>('/atendabot/whatsapp/connect', {
    phone_number: phoneNumber,
  });
};

export const disconnectWhatsApp = async () => {
  const response = await api.delete('/atendabot/whatsapp');
  return response.data as { status: string };
};

export const changeWhatsAppPhone = async (phoneNumber: string) => (
  patchData<{
    configured: boolean;
    connected: boolean;
    smartphoneConnected: boolean;
    status: string;
    provider_status?: string | null;
    phone_number: string;
    trial_expires_at?: string | null;
    is_subscribed: boolean;
    phone_change_allowed: boolean;
    changed: boolean;
  }>('/atendabot/whatsapp/phone', { phone_number: phoneNumber })
);

export const fetchZapiQrCode = async (restauranteId?: number | string) => {
  const data = await getData<{ image: string | { value?: string } }>('/atendabot/qr-code', {
    params: restauranteId ? { restaurante_id: restauranteId } : undefined,
  });
  const rawImage = (data?.image as any)?.value || (data?.image as any);
  return { image: rawImage as string | undefined };
};

export const fetchRestaurantById = async (restauranteId: number | string) => {
  return getData(`/manager/restaurantes/${restauranteId}`);
};

export const updateBotStatus = async (status: BotStatus): Promise<BotStatus> => {
  await api.put('/atendabot/config', { atendabot_active: status.isActive });
  return status;
};

export const fetchConversationsDashboard = async (): Promise<Conversation[]> => {
  const data = await getData<any[]>('/atendabot/conversas-atendabot');
  return data.map(mapConversation);
};

export const fetchConversations = async (): Promise<Conversation[]> => {
  const data = await getData<any[]>('/atendabot/conversas-atendabot-conversas');
  return data.map(mapConversation);
};

export const fetchRecentConversations = async (): Promise<Conversation[]> => {
  const data = await getData<any[]>('/atendabot/ultimas-conversas-atendabot');
  return data.map(mapConversation);
};

export const markAsSeen = async (clienteId: string): Promise<void> => {
  await api.put(`/atendabot/clientes/${clienteId}/visualizar`, {});
};

export const refreshClientAvatar = async (
  clienteId: string | number,
  force = true,
): Promise<string | null> => {
  const data = await postData<{ photo?: string | null }>(
    `/atendabot/clientes/${clienteId}/foto/atualizar`,
    { force },
  );
  return typeof data?.photo === 'string' && data.photo.trim() ? data.photo : null;
};

export const fetchMessages = async (clienteId: string): Promise<Message[]> => {
  const response = await api.get(`/atendabot/conversas/${clienteId}/mensagens`);
  const mensagens = Array.isArray(response.data) ? response.data : response.data?.mensagens || [];

  return mensagens.map((msg: any, index: number) => {
    const fromBotValue = msg.from_bot ?? msg.fromBot;
    const fromMeValue = msg.from_me ?? msg.fromMe;

    return {
      id: String(msg.id ?? index),
      conversationId: clienteId,
      content: msg.content ?? msg.message ?? '',
      timestamp: msg.timestamp ?? msg.date ?? '',
      imageUrl: msg.imageUrl ?? msg.image_url ?? null,
      audioUrl: msg.audioUrl ?? msg.audio_url ?? null,
      audioType: msg.audioType ?? msg.audio_type ?? null,
      sender: msg.sender ?? (fromBotValue ? 'bot' : fromMeValue ? 'user' : 'client'),
      fromBot: Boolean(fromBotValue),
      fromMe: Boolean(fromMeValue),
    };
  });
};

export const clearConversation = async (clienteId: string) => {
  await api.delete(`/manager/clientes/${clienteId}/messages`);
};

export const sendMessage = async (conversationId: string, content: string): Promise<Message> => {
  const data = await postData<Message>(`/atendabot/conversas/${conversationId}/mensagens`, { mensagem: content });
  return {
    ...data,
    id: String(data.id),
    conversationId,
    fromMe: true,
  };
};

export const toggleBotForConversation = async (clienteId: string, active: boolean): Promise<Conversation> => {
  await api.put(`/atendabot/clientes/${clienteId}/bot-status`, { active });

  return {
    id: clienteId,
    name: '',
    phoneNumber: '',
    lastMessage: '',
    lastMessageTime: '',
    lastMessageDate: '',
    chat_name: '',
    apelido: '',
    photo: '',
    botActive: active,
    transbordo: false,
  };
};

export const updateClientNickname = async (clienteId: string, apelido: string | null): Promise<void> => {
  await api.put(`/atendabot/clientes/${clienteId}/apelido`, { apelido });
};

export const blockContactByPhone = async (telefone: string, nome?: string | null, motivo?: string | null): Promise<void> => {
  await api.post('/atendabot/bloqueados', { telefone, nome: nome ?? null, motivo: motivo ?? null });
};

export const unblockContactByPhone = async (telefone: string): Promise<boolean> => {
  const blockedList = await getData<any[]>('/atendabot/bloqueados');
  const targetKey = toDddLast8Key(telefone);
  const match = blockedList.find((item: any) => toDddLast8Key(item.telefone) === targetKey);
  if (!match?.id) return false;

  await api.delete(`/atendabot/bloqueados/${match.id}`);
  return true;
};

export const fetchPromotions = async (): Promise<Record<string, Promotion>> => {
  await delay(900);
  return mockPromotions;
};

export const savePromotions = async (promotions: Record<string, Promotion>): Promise<Record<string, Promotion>> => {
  await delay(800);
  Object.assign(mockPromotions, promotions);
  return mockPromotions;
};

export const fetchMenu = async (): Promise<MenuItem[]> => {
  return getData('/atendabot/cardapio');
};

export const syncMenu = async (): Promise<MenuItem[]> => fetchMenu();

export const saveSettings = async (settings: RestaurantSettings) => {
  const response = await api.put('/atendabot/config', settings);
  return response.data;
};

export const validateSaiposConnection = async (idPartner: string) => (
  postData<{ connected: boolean; message?: string }>('/atendabot/saipos/validate', { idPartner })
);

export const fetchPromptConfig = async () => getData('/atendabot/prompt-config');

export type OrderControlSettings = {
  pedidos_habilitados: boolean;
};

export const fetchOrderControlSettings = async (): Promise<OrderControlSettings> =>
  getData('/atendabot/pedidos/configuracao');

export const updateOrderControlSettings = async (
  settings: OrderControlSettings,
): Promise<OrderControlSettings> =>
  putData('/atendabot/pedidos/configuracao', settings);

export type ManagerIntegrationSettings = {
  usar_reservo_reservas: boolean;
  usar_feedbackhero_avaliacao_pedidos: boolean;
  usar_cv_vagas: boolean;
};

export const fetchManagerIntegrationSettings = async (): Promise<ManagerIntegrationSettings> =>
  getData('/atendabot/prompt-config/integrations');

export const updateManagerIntegrationSettings = async (
  settings: ManagerIntegrationSettings,
): Promise<ManagerIntegrationSettings> =>
  putData('/atendabot/prompt-config/integrations', settings);

export const geocodePromptAddress = async (address: string) => {
  return getData('/config/geocode', {
    params: { address },
  });
};

export const savePromptConfig = async (data: any) => putData('/atendabot/prompt-config', data);

export const savePromptDraft = async (data: any) => putData('/atendabot/prompt-config/draft', data);

const fetchCoreModuleRule = async (moduleId: string) => {
  const list = await getData<any[]>('/config/core-config/module-rules');
  return list.find((item: any) => item?.id === moduleId) || null;
};

const updateCoreModuleRuleSetting = async (moduleId: string, usarRegraGlobal: boolean) => {
  return putData(`/config/core-config/module-rules/${moduleId}`, {
    usar_regra_global: usarRegraGlobal,
  });
};

export const fetchAtendabotModuleRule = async () => fetchCoreModuleRule('atendabot');

export const updateAtendabotModuleRule = async (usarRegraGlobal: boolean) =>
  updateCoreModuleRuleSetting('atendabot', usarRegraGlobal);

export const testPromptConfigChat = async (payload: {
  message: string;
  history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  config?: any;
}) => postData('/atendabot/prompt-config/test-chat', payload);

export const fetchSelectedSubnicho = async () => {
  const response = await fetch('/api/settings/subnicho');
  if (!response.ok) throw new Error('Erro ao buscar subnicho selecionado');
  return response.json();
};

export const fetchFormCategories = async () => getData('/atendabot/forms/categories');

export const fetchFormQuestions = async () => getData('/atendabot/forms/questions');

export const fetchQuestionOptions = async (questionId: number) =>
  getData(`/atendabot/forms/questions/${questionId}/options`);

export const fetchConditionals = async () => getData('/atendabot/forms/conditional');

export const fetchNichos = async () => getData('/atendabot/nichos');

export const fetchSubnichos = async (nichoId: number) =>
  getData(`/atendabot/nichos/${nichoId}/subnichos`);

export const fetchFormResponses = async () => getData('/atendabot/forms/respostas-cliente');

export const fetchFormResponse = async (id: number) =>
  getData(`/atendabot/forms/respostas-cliente/${id}`);

export const createFormResponse = async (data: any) =>
  postData('/atendabot/forms/respostas-cliente', data);

export const updateFormResponse = async (id: number, data: any) =>
  putData(`/atendabot/forms/respostas-cliente/${id}`, data);

export const deleteFormResponse = async (id: number) =>
  deleteData(`/atendabot/forms/respostas-cliente/${id}`);

export const fetchFormQuestionsBySubnicho = async (id_subnicho: number) => {
  const response = await api.get(`/formulario/perguntas/subnicho/${id_subnicho}`);
  return response.data;
};

export const fetchInfoExtras = async () => getData('/atendabot/forms/info-extra');

export const createInfoExtra = async (data: any) => postData('/atendabot/forms/info-extra', data);

export const updateInfoExtra = async (id: number, data: any) =>
  putData(`/atendabot/forms/info-extra/${id}`, data);

export const deleteInfoExtra = async (id: number) =>
  deleteData(`/atendabot/forms/info-extra/${id}`);

export const fetchShortcuts = async (_clienteId: number) => getData('/atendabot/atalhos');

export const createShortcut = async (data: any) => postData('/atendabot/atalhos', data);

export const updateShortcut = async (id: number, data: any) =>
  putData(`/atendabot/atalhos/${id}`, data);

export const deleteShortcut = async (id: number) =>
  deleteData(`/atendabot/atalhos/${id}`);

export const toggleShortcut = async (id: number, ativo: boolean) =>
  patchData(`/atendabot/atalhos/${id}/ativar`, { ativo });

const agendaBase = '/atendabot/agenda';

export const fetchAgendaConfig = async (): Promise<AgendaConfig> =>
  getData(`${agendaBase}/configuracao`);

export const updateAgendaConfig = async (data: Partial<Pick<
  AgendaConfig,
  'timezone' | 'slotIntervalMinutes' | 'defaultDurationMinutes'
>>): Promise<AgendaConfig> => putData(`${agendaBase}/configuracao`, data);

export const fetchAgendaServices = async (includeInactive = true): Promise<AgendaService[]> =>
  getData(`${agendaBase}/servicos`, { params: { includeInactive } });

export const createAgendaService = async (data: {
  name: string;
  description?: string | null;
  durationMinutes: number;
  price?: number | null;
  color: string;
}): Promise<AgendaService> => postData(`${agendaBase}/servicos`, data);

export const updateAgendaService = async (
  id: string,
  data: Partial<Omit<AgendaService, 'id' | 'isDefault' | 'active' | 'createdAt' | 'updatedAt'>>,
): Promise<AgendaService> => putData(`${agendaBase}/servicos/${id}`, data);

export const setAgendaServiceActive = async (id: string, active: boolean): Promise<AgendaService> =>
  patchData(`${agendaBase}/servicos/${id}`, { active });

export const fetchAgendaProfessionals = async (includeInactive = true): Promise<AgendaProfessional[]> =>
  getData(`${agendaBase}/profissionais`, { params: { includeInactive } });

export const createAgendaProfessional = async (data: {
  name: string;
  description?: string | null;
  color: string;
  serviceIds: string[];
}): Promise<AgendaProfessional> => postData(`${agendaBase}/profissionais`, data);

export const updateAgendaProfessional = async (
  id: string,
  data: Partial<Pick<AgendaProfessional, 'name' | 'description' | 'color' | 'serviceIds'>>,
): Promise<AgendaProfessional> => putData(`${agendaBase}/profissionais/${id}`, data);

export const setAgendaProfessionalActive = async (
  id: string,
  active: boolean,
): Promise<AgendaProfessional> => patchData(`${agendaBase}/profissionais/${id}`, { active });

export const updateAgendaProfessionalServices = async (
  id: string,
  serviceIds: string[],
): Promise<AgendaProfessional> => putData(`${agendaBase}/profissionais/${id}/servicos`, { serviceIds });

export const fetchAgendaProfessionalAvailability = async (
  id: string,
): Promise<AgendaAvailabilityPeriod[]> => getData(`${agendaBase}/profissionais/${id}/disponibilidade`);

export const updateAgendaProfessionalAvailability = async (
  id: string,
  periods: AgendaAvailabilityPeriod[],
): Promise<{ periods: AgendaAvailabilityPeriod[]; warnings: string[] }> =>
  putData(`${agendaBase}/profissionais/${id}/disponibilidade`, { periods });

export const fetchAgendaExceptions = async (params: {
  from?: string;
  to?: string;
  professionalId?: string;
} = {}): Promise<AgendaException[]> => getData(`${agendaBase}/excecoes`, { params });

export const createAgendaException = async (data: {
  professionalId?: string | null;
  type: 'blocked' | 'extra';
  startAt: string;
  endAt: string;
  reason?: string | null;
}): Promise<AgendaException> => postData(`${agendaBase}/excecoes`, data);

export const updateAgendaException = async (
  id: string,
  data: Partial<Pick<AgendaException, 'professionalId' | 'type' | 'startAt' | 'endAt' | 'reason'>>,
): Promise<AgendaException> => putData(`${agendaBase}/excecoes/${id}`, data);

export const deleteAgendaException = async (id: string): Promise<void> =>
  deleteData(`${agendaBase}/excecoes/${id}`);

export const fetchAgendaAvailability = async (params: {
  date: string;
  serviceId: string;
  professionalId?: string;
  appointmentId?: string;
}): Promise<AgendaAvailabilityResponse> => getData(`${agendaBase}/disponibilidade`, { params });

export const fetchAgendaAppointments = async (params: {
  from: string;
  to: string;
  professionalId?: string;
  serviceId?: string;
  status?: string;
}): Promise<AgendaAppointment[]> => getData(`${agendaBase}/agendamentos`, { params });

export const fetchAgendaAppointment = async (id: string): Promise<AgendaAppointment> =>
  getData(`${agendaBase}/agendamentos/${id}`);

export const createAgendaAppointment = async (
  data: AgendaAppointmentPayload,
): Promise<AgendaAppointment> => postData(
  `${agendaBase}/agendamentos`,
  data,
  data.idempotencyKey ? { headers: { 'Idempotency-Key': data.idempotencyKey } } : undefined,
);

export const updateAgendaAppointment = async (
  id: string,
  data: Partial<AgendaAppointmentPayload>,
): Promise<AgendaAppointment> => putData(`${agendaBase}/agendamentos/${id}`, data);

export const updateAgendaAppointmentStatus = async (
  id: string,
  status: AgendaAppointmentStatus,
): Promise<AgendaAppointment> => patchData(`${agendaBase}/agendamentos/${id}/status`, { status });

export const fetchTransbordos = async () => getData('/atendabot/transbordo');

export const createTransbordo = async (data: {
  motivo: string;
  mensagem_cliente?: string;
  mensagem_transbordo?: string;
  pausar_bot?: boolean;
  phones: string[];
}) => postData('/atendabot/transbordo', data);

export const updateTransbordo = async (id: number, data: {
  motivo: string;
  mensagem_cliente?: string;
  mensagem_transbordo?: string;
  pausar_bot?: boolean;
  phones: string[];
}) => putData(`/atendabot/transbordo/${id}`, data);

export const deleteTransbordo = async (id: number) =>
  deleteData(`/atendabot/transbordo/${id}`);
